// React binding for the floor outbox (spec 06 §9.2, D-048).
//
// ONE QUEUE PER DEVICE, not per screen. The outbox is device state, not screen
// state: a cut queued on the unit card must still drain while the operator is
// three screens away filling in a count worksheet, and the pending badge in the
// chrome must be the same number everywhere. So the instance is a module-level
// singleton created on first use, and every screen subscribes to it through
// `useSyncExternalStore` (React 19's tearing-safe external-store bridge — the
// queue mutates outside React and a plain useState mirror would go stale under
// concurrent rendering).

import { subscribeSuspension, tenantWritesPaused } from "../../../lib/tenant-suspension";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { FloorOutbox, type OutboxSettlement, type OutboxSnapshot } from "../lib/floor-outbox";
import { memoryOutboxStore, openOutboxStore, type OutboxStore } from "../lib/outbox-store";
import { memoryPhotoStore, openPhotoStore, type PhotoStore } from "../lib/photo-store";
import { createPhotoUploader } from "../lib/photo-upload";
import { clearFloorReadCache } from "../lib/floor-read-cache";
import { forgetFloorReads } from "./use-cached-read";
import type { QueryClient } from "@tanstack/react-query";
import type { FloorOp } from "../lib/floor-ops";

let instance: FloorOutbox | undefined;
let suspensionSubscribed = false;
let photoStoreInstance: PhotoStore | undefined;
let started = false;

/**
 * The device's outbox. Created against a memory store synchronously (so the
 * first render has something to subscribe to) and UPGRADED to IndexedDB as soon
 * as the async open resolves — the upgrade re-hydrates from disk, so anything
 * queued before the swap is still there.
 *
 * Deliberately NOT a React context: a context would let a test render half the
 * app with a different queue than the chrome is showing, which is the exact
 * class of bug that makes an offline indicator lie.
 */
export function floorOutbox(): FloorOutbox {
  if (instance === undefined) {
    // `durable` starts PESSIMISTIC and is corrected upward once the IndexedDB
    // open actually succeeds. The wrong way round would be a lie in the one
    // direction that matters: telling an operator their queue is safe before
    // knowing that it is.
    const outbox = new FloorOutbox({
      store: pendingStore((durable) => {
        outbox.setDurable(durable);
      }),
      durable: false,
      // The evidence-photo pipeline (D-096): blobs from the durable photo
      // store, uploaded through the real three-call flow when a flag drains.
      photos: createPhotoUploader(floorPhotoStore()),
      // A suspended tenant takes no writes (spec 15 §3.2): the queue holds its
      // work exactly as it does with no signal, and sends it once the
      // suspension is over (D-495 ruling 3). Nothing is parked or lost.
      isOnline: () =>
        (typeof navigator === "undefined" ? true : navigator.onLine) && !tenantWritesPaused(),
    });
    instance = outbox;
    subscribeOnceToSuspension();
  }
  return instance;
}

/** Send the queue the moment a suspension clears (D-495 ruling 3). One
 * listener for the module's life, and it reaches the CURRENT queue: a
 * listener holding the queue it was made with kept sending from a queue a
 * device hand-over had stopped and dropped, alongside the new one, and a new
 * listener piled up per hand-over (D-629 ruling 7). */
function subscribeOnceToSuspension(): void {
  if (suspensionSubscribed) return;
  suspensionSubscribed = true;
  subscribeSuspension(() => {
    if (!tenantWritesPaused()) void instance?.sendNow();
  });
}

/**
 * The device's durable photo store (D-096) — the binary sibling of the outbox,
 * same database, same pending-facade pattern so a capture in the first
 * milliseconds cannot land in a store about to be discarded. The capture path
 * (op-run's defect panel) persists blobs here FIRST, then enqueues the flag
 * that references them.
 */
export function floorPhotoStore(): PhotoStore {
  if (photoStoreInstance === undefined) {
    const fallback = memoryPhotoStore();
    let resolved: PhotoStore | undefined;
    const ready = openPhotoStore()
      .then(async (opened) => {
        for (const record of await fallback.readAll()) await opened.store.put(record);
        resolved = opened.store;
        return opened.store;
      })
      .catch(() => {
        resolved = fallback;
        return fallback;
      });
    const pickStore = async (): Promise<PhotoStore> => resolved ?? (await ready);
    photoStoreInstance = {
      get: async (photoId) => (await pickStore()).get(photoId),
      put: async (record) => (await pickStore()).put(record),
      remove: async (photoId) => (await pickStore()).remove(photoId),
      readAll: async () => (await pickStore()).readAll(),
      clear: async () => (await pickStore()).clear(),
    };
  }
  return photoStoreInstance;
}

/**
 * A store facade that starts in memory and swaps to IndexedDB once open. Every
 * call is queued behind the same promise, so a write issued in the first
 * milliseconds cannot land in the store that is about to be discarded — and the
 * real durability is reported back through `onDurability` the moment it is
 * known, because the chrome's "this device cannot save your queue" warning is
 * only useful if it is TRUE.
 */
function pendingStore(onDurability: (durable: boolean) => void): OutboxStore {
  const fallback = memoryOutboxStore();
  let resolved: OutboxStore | undefined;
  const ready = openOutboxStore()
    .then(async (opened) => {
      // Carry anything already queued into the durable store before switching.
      const buffered = await fallback.readAll();
      for (const record of buffered) await opened.store.put(record);
      resolved = opened.store;
      onDurability(opened.durable);
      return opened.store;
    })
    .catch(() => {
      resolved = fallback;
      onDurability(false);
      return fallback;
    });
  const pick = async (): Promise<OutboxStore> => resolved ?? (await ready);
  return {
    readAll: async () => (await pick()).readAll(),
    put: async (record) => (await pick()).put(record),
    remove: async (key) => (await pick()).remove(key),
    clear: async () => (await pick()).clear(),
  };
}

/** Subscribe to the queue. Starts the connectivity/focus triggers once. */
export function useFloorOutbox(): OutboxSnapshot {
  const outbox = floorOutbox();
  useEffect(() => {
    if (started) return;
    started = true;
    outbox.start();
  }, [outbox]);
  return useSyncExternalStore(
    (listener) => outbox.subscribe(listener),
    () => outbox.snapshot(),
    () => outbox.snapshot(),
  );
}

export type SubmitState =
  | { readonly phase: "idle" }
  | { readonly phase: "submitting" }
  | { readonly phase: "settled"; readonly settlement: OutboxSettlement }
  /**
   * THE TAP WAS NOT SAVED. Not "failed on the server" — never reached the
   * device's own queue, because the durable write rejected (quota exhausted on
   * a full phone, a store the browser tore down). The screen must say so
   * loudly: this is the one outcome where the operator has to redo the work,
   * and the only thing worse than losing the tap is telling them it landed.
   */
  | { readonly phase: "not_saved"; readonly error: unknown };

/**
 * The one write hook every floor screen uses. It ALWAYS goes through the queue
 * (rule 1 in floor-outbox.ts) and reports the outcome the screen must render:
 * `applied` with the server's answer, `queued` when there is no network,
 * `needs_attention` with the failure the tray is now holding — or `not_saved`
 * when the queue itself refused the write.
 *
 * It NEVER throws: a floor screen that has to wrap every button in try/catch
 * will eventually forget one, and the forgotten one silently swallows a cut.
 */
export function useFloorSubmit(): {
  state: SubmitState;
  submit: (
    op: FloorOp,
    timeoutMs?: number,
    meta?: { identity?: string | undefined; photoIds?: readonly string[] | undefined },
  ) => Promise<OutboxSettlement | undefined>;
  reset: () => void;
} {
  const outbox = floorOutbox();
  const [state, setState] = useState<SubmitState>({ phase: "idle" });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const submit = useCallback(
    async (
      op: FloorOp,
      timeoutMs?: number,
      meta?: { identity?: string | undefined; photoIds?: readonly string[] | undefined },
    ): Promise<OutboxSettlement | undefined> => {
      setState({ phase: "submitting" });
      try {
        const settlement = await outbox.enqueueAndWait(op, timeoutMs, meta);
        if (alive.current) setState({ phase: "settled", settlement });
        return settlement;
      } catch (error) {
        if (alive.current) setState({ phase: "not_saved", error });
        return undefined;
      }
    },
    [outbox],
  );

  const reset = useCallback(() => {
    setState({ phase: "idle" });
  }, []);

  return { state, submit, reset };
}

/**
 * Sign-out / device hand-over.
 *
 * A shared floor tablet changes hands every shift. Anything left behind is
 * either attributed to the wrong operator (the queue) or shown to someone with
 * no grant for it (the cached worksheets and unit cards). The queue is only
 * dropped once the caller has confirmed it is empty or the operator has decided
 * — `pendingWork()` is how the sign-out button asks.
 */
export function floorPendingWorkCount(): number {
  const snapshot = floorOutbox().snapshot();
  return snapshot.pendingCount + snapshot.attentionCount;
}

export async function resetFloorDeviceState(queryClient?: QueryClient): Promise<void> {
  // Purge and close the read cache FIRST, before anything below awaits: from
  // here on no floor read may write to the device, whatever a still-mounted
  // screen does, until a sign-in reopens it (D-635).
  clearFloorReadCache();
  if (queryClient !== undefined) forgetFloorReads(queryClient);
  try {
    await floorOutbox().clear();
  } catch {
    // A store that will not clear must not block the sign-out.
  }
  try {
    // Belt over the engine's per-item discard: hand-over wipes the WHOLE photo
    // store, orphans included — another worker's evidence must not survive on
    // a shared tablet (D-096, same rule as the queue and the read cache).
    await floorPhotoStore().clear();
  } catch {
    // Same posture as the queue clear above.
  }
  instance?.stop();
  instance = undefined;
  photoStoreInstance = undefined;
  started = false;
}

/** Test seam: replace the singleton (and its listeners) with a fresh queue. */
export function __setFloorOutboxForTests(next: FloorOutbox | undefined): void {
  instance?.stop();
  instance = next;
  started = false;
}
